import { AppBar, Box, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { FC, ReactNode, useState, useEffect } from 'react';
import { Store } from 'react-notifications-component';
import { useSelector } from 'react-redux';
import { Route, useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import CarofiLogo from '@/assets/svgs/carofi-logo.icon.svg?react';
import { FirebaseMessagingInitializer } from '@/shared/components/core-ui/firebase-messaging-initializer';
import { LanguageSwitcher } from '@/shared/components/core-ui/language-switcher';
import GlobalHeader from '@/shared/components/group-ui/global-header/global-header.component';
import { NotificationPermissionBanner } from '@/shared/components/group-ui/notification-permission-banner';
import SideMenu from '@/shared/components/group-ui/side-menu/side-menu.component';
import { UserRole } from '@/shared/enums/user-role.enum';
import { UserTeams } from '@/shared/enums/user-teams.enum';
import { useLocalization } from '@/shared/hooks/useLocalization';
import { ProtectedRoute } from '@/shared/layouts/protected-route/protected-route.component';
import { RootState } from '@/shared/redux/reducers';
import { unregisterDeviceFromNotifications } from '@/shared/services/firebase/notifications.service';
import { cleanupUserData } from '@/shared/services/helpers/auth-helper/auth-helper.service';
import { resetMessagingState } from '@/shared/services/utils/firebase/messaging.service';

/**
 * interface DefaultLayoutComponentProps
 */
interface DefaultLayoutComponentProps {
  children?: ReactNode;
  logoutHandler: () => void;
  addLayout?: boolean;
}

/**
 * functional component to render child route in Default layout
 * @param childern react element to be injected in component
 * @param userEmail user email
 * @param logoutHandler to handle user logout action
 */
const DefaultLayoutComponent: React.FC<DefaultLayoutComponentProps> = ({
  children,
  logoutHandler,
  addLayout
}) => {
  const theme = useTheme();
  const { isRTLDirect: isRTL } = useLocalization();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { accessToken, userId } = useSelector((state: RootState) => ({
    accessToken: state.auth.accessToken,
    userId: state.auth.id
  }));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Reset messaging state when user logs out (accessToken becomes null)
  useEffect(() => {
    if (!accessToken) {
      // Clean up messaging state and unregister from server
      resetMessagingState();
      unregisterDeviceFromNotifications();
    }
  }, [accessToken]);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Initialize Firebase messaging for authenticated users */}
      {accessToken && (
        <FirebaseMessagingInitializer
          userId={userId}
          enabled={true}
          autoRegister={true}
          useCustomBanner={true}
          onMessage={(payload) => {
            console.log('Received push notification:', payload);
          }}
          onTokenReceived={(token) => {
            console.log('FCM token received:', token);
          }}
        />
      )}

      {addLayout ? (
        <AppBar sx={{ height: '70px !important' }}>
          <Toolbar disableGutters sx={{ direction: isRTL ? 'rtl' : 'ltr', display: 'flex' }}>
            <Box aria-label="logo" sx={{ mt: 1.375, flexGrow: 1 }}>
              <CarofiLogo style={{ width: '120px', height: '35px' }} />
            </Box>
            <LanguageSwitcher />
          </Toolbar>
        </AppBar>
      ) : (
        <>
          <GlobalHeader eventHandlers={{ logoutHandler, toggleMobileMenu: handleDrawerToggle }} />
          <SideMenu mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        </>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: theme.palette.background.default,
          height: 'auto',
          [theme.breakpoints.up('sm')]: {
            mt: theme.spacing(8),
            width: 'calc(100vw - 266px)',
            marginLeft: isRTL ? 0 : 'auto',
            marginRight: isRTL ? 'auto' : 0
          },
          [theme.breakpoints.up('xs')]: {
            mt: theme.spacing(7),
            width: '100vw'
          }
        }}
      >
        {/* Show notification permission banner when user is authenticated */}
        {accessToken && (
          <Box sx={{ px: 3, pt: 2 }}>
            <NotificationPermissionBanner
              onPermissionGranted={() => {
                console.log('User granted notification permission');
              }}
              onPermissionDenied={() => {
                console.log('User denied notification permission');
              }}
            />
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

/**
 * interface DefaultRouteProps
 */
interface DefaultRouteProps {
  component: FC<any>;
  // path: string;
  authenticationRequired: boolean;
  permittedList?: UserRole[];
  permittedTeamsList?: UserTeams[];
  // userEmail?: string;
  // userRole?: UserRole;
  // isAuthenticated: boolean;
  addLayout?: boolean;
  children?: ReactNode;
}

/**
 * functional component to handle rendering routes in default layout
 * @param component react component to be rendered
 * @param userEmail user email
 * @param isAuthenticated user authentication status
 * @param authenticationRequired route config whether route will be protected or not
 */
const DefaultRoute: React.FC<DefaultRouteProps> = ({
  component: Component,
  // userEmail,
  permittedList,
  permittedTeamsList,
  // userRole,
  // isAuthenticated,
  authenticationRequired,
  children,
  addLayout,
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();

  const logoutUser = () => {
    Store.addNotification({
      title: t('SHARED.HEADER.NOTIFICATIONS.LOGOUT_SUCCESS.TITLE'),
      message: t('SHARED.HEADER.NOTIFICATIONS.LOGOUT_SUCCESS.MESSAGE'),
      type: 'success',
      insert: 'top',
      container: 'top-center',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 3000,
        onScreen: true
      }
    });
    setTimeout(() => {
      cleanupUserData();
      navigate(location.pathname);
    }, 1500);
  };
  return authenticationRequired ? (
    <ProtectedRoute
      permittedList={permittedList}
      permittedTeamsList={permittedTeamsList}
      component={(matchProps) => (
        <DefaultLayoutComponent addLayout={addLayout} logoutHandler={logoutUser}>
          <Component {...matchProps} />
        </DefaultLayoutComponent>
      )}
    />
  ) : (
    <Route {...rest}>
      <DefaultLayoutComponent addLayout={addLayout} logoutHandler={logoutUser}>
        <Component {...rest} />
      </DefaultLayoutComponent>
    </Route>
  );
};

/**
 * function to to map redux state to component props
 * @param state root state or redux
 */
// function mapStateToProps(state: RootState) {
//   console.log('state.auth', state.auth);
//   return {
//     isAuthenticated: !!state.auth.accessToken,
//     userRole: state.auth.role,
//     userEmail: state.auth.email
//   };
// }

export default DefaultRoute;
